import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { restaurantScopedStorage } from '@/shared/store/restaurantScopedStorage'
import {
  DEFAULT_GOOGLE_FROM_STARS,
  REVIEW_INVITE_SCHEMA_VERSION,
  REVIEW_STARS
} from '../config'
import { ReviewInvite } from '../types/feedback.model'

interface ReviewInviteState extends ReviewInvite {
  setIsActive: (value: boolean) => void
  setGoogleFromStars: (value: number) => void
  /** Replaces the whole document, for when the API starts hydrating this. */
  importInvite: (invite: ReviewInvite) => void
}

const isStarCount = (value: unknown): value is number =>
  Number.isInteger(value) &&
  (value as number) >= 1 &&
  (value as number) <= REVIEW_STARS

export const useReviewInviteStore = create<ReviewInviteState>()(
  persist(
    set => ({
      /*
        On from the start, so a new venue is collecting rather than having to
        find a switch first. Being on without a Google place connected is not
        a silent failure: the page says plainly that no guest gets a link yet.
      */
      isActive: true,
      googleFromStars: DEFAULT_GOOGLE_FROM_STARS,

      setIsActive: value => set({ isActive: value }),

      setGoogleFromStars: value => set({ googleFromStars: value }),

      importInvite: invite =>
        set({
          isActive: invite.isActive,
          googleFromStars: invite.googleFromStars
        })
    }),
    {
      name: 'review-invite-storage',
      storage: createJSONStorage(() => restaurantScopedStorage),
      version: REVIEW_INVITE_SCHEMA_VERSION,
      partialize: state => ({
        isActive: state.isActive,
        googleFromStars: state.googleFromStars
      }),
      /*
        Before v4 `rules` sat beside the switch: a star threshold
        (`positiveFromStars`) and, from v2, the place picked out of Google's
        suggestions. v4 dropped both. The threshold is back in v5 as
        `googleFromStars`, so a venue that set one gets its number back; the
        place stays gone (see the version note in config.ts).
      */
      migrate: persisted => {
        const state = persisted as
          | (Partial<ReviewInvite> & {
              rules?: { positiveFromStars?: unknown }
            })
          | undefined
        const oldThreshold = state?.rules?.positiveFromStars
        return {
          isActive: state?.isActive ?? true,
          googleFromStars: isStarCount(oldThreshold)
            ? oldThreshold
            : DEFAULT_GOOGLE_FROM_STARS
        } as ReviewInviteState
      }
    }
  )
)
